import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  Segment,
  Header,
  Label,
  Loader,
  Message,
  Button,
  Icon,
  Divider,
} from 'semantic-ui-react';
import LocalStorage from '../../../lib/LocalStorage';
import config from '../../../common/repository/config';

interface DailyStatus {
  service_type: string;
  service_status: string;
}

interface Case {
  _id: string;
  claim_number: string;
  insurance_company_name: string;
  case_type_name: string;
  case_id: string;
  case_inward_date: string;
  daily_status: DailyStatus[];
}

interface AllocatorGroup {
  allocator_name: string;
  count: string;
  cases: Case[];
}

const DailyStatusPendingList: React.FC = () => {
  const [data, setData] = useState<AllocatorGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const history = useHistory();

  useEffect(() => {
    const fetchlistPendingStatus = async () => {
      setLoading(true);
      setError('');
      try {
        const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
        if (!token) {
          setError('Authentication token is missing.');
          return;
        }

        const response = await fetch(`${config.apiBaseURL}/notification/listPendingStatus`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': token,
          },
        });

        if (!response.ok) throw new Error('Failed to fetch daily status list');

        const resData = await response.json();
        setData(resData?.data?.items || []);
      } catch (e) {
        console.error('Error fetching pending status:', e);
        setError('Something went wrong while fetching data.');
      } finally {
        setLoading(false);
      }
    };

    fetchlistPendingStatus();
  }, []);

  return (
    <Segment padded>
      <Header as="h2" dividing color="blue" style={{ marginBottom: '1.5rem' }}>
        <Icon name="clipboard list" />
        Pending Daily Status List
      </Header>

      {loading && <Loader active inline="centered" content="Loading..." />}
      {error && <Message negative content={error} />}
      {!loading && !error && data.length === 0 && (
        <Message info content="No pending daily status found." />
      )}

      {!loading &&
        !error &&
        data.map((group, index) => (
          <Segment key={index} color="blue" raised>
        <Header as="h4" color="blue" dividing>
        <Icon name="user circle" />
        <span style={{ marginRight: '1em' }}>Allocator: {group.allocator_name}</span>
        <span style={{color:"#008000"}}>Case Count: {group.count}</span>
       </Header>

           
            <div style={{ overflowX: 'auto' }}>
              <Table celled striped compact>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Claim Number</Table.HeaderCell>
                    <Table.HeaderCell>Insurance</Table.HeaderCell>
                    <Table.HeaderCell>Case Type</Table.HeaderCell>
                    <Table.HeaderCell>Case Inward Date</Table.HeaderCell>
                    <Table.HeaderCell>Daily Status (Type - Status)</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Action</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {group.cases.map((caseItem) => (
                    <Table.Row key={caseItem._id}>
                      <Table.Cell>{caseItem.claim_number}</Table.Cell>
                      <Table.Cell>{caseItem.insurance_company_name}</Table.Cell>
                      <Table.Cell>{caseItem.case_type_name}</Table.Cell>
                      <Table.Cell>
                        {new Date(caseItem.case_inward_date).toLocaleDateString()}
                      </Table.Cell>
                      <Table.Cell>
                        <Label.Group style={{ gap: '6px', flexWrap: 'wrap', display: 'flex' }}>
                          {caseItem.daily_status.map((status, idx) => {
                            const isPending = status.service_status.toLowerCase() === 'pending';
                            const labelStyle = isPending
                              ? { backgroundColor: 'rgb(194 66 67)', color: 'white' }
                              : { backgroundColor: 'rgb(19, 168, 54)', color: 'white' };

                            return (
                              <Label
                                key={idx}
                                size="small"
                                style={{
                                  ...labelStyle,
                                  borderRadius: '1rem',
                                  padding: '0.5em 0.75em',
                                }}
                              >
                                {status.service_type} - {status.service_status}
                              </Label>
                            );
                          })}
                        </Label.Group>
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <Button
                          size="small"
                          icon
                          style={{
                            backgroundColor: '#DEFF8B',
                            color: 'black',
                          }}
                          onClick={() => history.push(`/case/view/${caseItem.case_id}`)}
                        >
                          Open
                        </Button>
                      </Table.Cell>

                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </Segment>
        ))}
    </Segment>
  );
};

export default DailyStatusPendingList;
